"use strict";
// Copyright © Veeam Software Group GmbH
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.TreeNode = void 0;
const react_1 = __importStar(require("react"));
const fast_deep_equal_1 = __importDefault(require("fast-deep-equal"));
const styled_components_1 = __importStar(require("styled-components"));
const public_1 = require("../common/public");
const Checkbox_1 = require("../controls/Checkbox");
const measureText_1 = require("../helpers/measureText");
const Icon_1 = require("../Icon");
const icons_1 = require("../icons");
const Spinner_1 = require("../Spinner");
const StackView_1 = require("../StackView");
const StatusIcon_1 = require("../StatusIcon");
const Text_1 = require("../Text");
const Tooltip_1 = require("../Tooltip");
const nodeData_1 = require("./nodeData");
const View_1 = require("../View");
const arrowIcons_1 = require("./arrowIcons");
const WIDTH_ARROW_ICON = 10;
const Root = (0, styled_components_1.default)(StackView_1.StackView) `
    cursor: pointer;
    box-sizing: border-box;
    width: 100%;
    height: ${({ autoHeight, isCheckable, theme }) => autoHeight ? 'auto' : isCheckable ? theme.spacing.xl : theme.spacing.l};
    white-space: nowrap;

    background-color: ${({ highlight, theme }) => highlight ? theme.colorHighlightDark : 'transparent'};

    ${(props) => !props.disabled &&
    (0, styled_components_1.css) `
            &:hover {
                background-color: ${({ theme }) => theme.colorHighlightLight};
            }

            &.active,
            &.active:hover {
                background-color: ${({ theme }) => theme.colorHighlightDark};
            }
        `}

    padding-left: ${({ theme, isTerminal, depth }) => `${depth * WIDTH_ARROW_ICON +
    (isTerminal ? WIDTH_ARROW_ICON + 2 * parseInt(theme.spacing.s, 10) : 0)}px`};
`;
const ToggleView = (0, styled_components_1.default)(View_1.View) `
    ${({ theme }) => (0, styled_components_1.css) `
        width: calc(${WIDTH_ARROW_ICON}px + 2 * ${theme.spacing.s});
        height: ${theme.spacing.m};
    `}
`;
const CheckboxWrapper = (0, styled_components_1.default)(View_1.View) `
    padding-right: ${({ theme }) => theme.spacing.s};
`;
const NodeDescriptionWrapper = styled_components_1.default.div `
    flex: 1 1 100%;
    width: 0;
    align-self: center;
    overflow: hidden;

    ${({ hideIcon, theme }) => !hideIcon &&
    (0, styled_components_1.css) `
            padding-left: ${theme.spacing.s};
        `}
`;
const onMouseEnterHandlerDecorator = (tooltipHandler, ref) => {
    return () => {
        const { originalWidth, fullWidth } = (0, measureText_1.measureText)(ref);
        if (fullWidth > originalWidth) {
            tooltipHandler();
        }
    };
};
const DefaultNodeDescription = ({ description, disabled, dataSelectorId }) => {
    const ref = (0, react_1.useRef)(null);
    return (0, Tooltip_1.useTooltip)({
        text: description,
        delayTimeout: 400,
        render: (tooltip) => (react_1.default.createElement(Text_1.Text, Object.assign({}, tooltip, { "data-selector-id": dataSelectorId, ref: ref, size: Text_1.TEXT_SIZE.m, view: disabled ? Text_1.TEXT_VIEW.light : undefined, truncate: true, onMouseEnter: onMouseEnterHandlerDecorator(tooltip.onMouseEnter, ref) }), description))
    });
};
const SPINNER_SIZE = 16;
class TreeNode extends react_1.default.Component {
    constructor(props) {
        super(props);
    }
    shouldComponentUpdate(nextProps) {
        const _a = this.props, { node } = _a, restProps = __rest(_a, ["node"]);
        const { node: nextNode } = nextProps, restNextProps = __rest(nextProps, ["node"]);
        if (!nodeData_1.NodeData.equal(node, nextNode)) {
            return true;
        }
        return !(0, fast_deep_equal_1.default)(restProps, restNextProps);
    }
    render() {
        const _a = this.props, { node, selected, onActivateNode, depth, renderer, autoHeight, iconRenderer, hideIcon, checkable, checkableConfig, checked, onCheckedNode, onContextMenu, disabled, expanded, onExpandNode, loading = false, warning, align = StackView_1.STACK_ALIGN.start, indeterminate } = _a, rest = __rest(_a, ["node", "selected", "onActivateNode", "depth", "renderer", "autoHeight", "iconRenderer", "hideIcon", "checkable", "checkableConfig", "checked", "onCheckedNode", "onContextMenu", "disabled", "expanded", "onExpandNode", "loading", "warning", "align", "indeterminate"]);
        const isTerminal = node.isTerminal();
        const renderNodeDescription = () => {
            var _a;
            const description = (_a = renderer === null || renderer === void 0 ? void 0 : renderer(node)) !== null && _a !== void 0 ? _a : `${node.id}`;
            return (react_1.default.createElement(NodeDescriptionWrapper, { hideIcon: hideIcon, "data-selector-id": `${rest['data-selector-id']}-description` }, typeof description === 'string' ? (react_1.default.createElement(DefaultNodeDescription, { description: description, disabled: disabled, dataSelectorId: `${rest['data-selector-id']}-text` })) : (description)));
        };
        const defaultIcon = isTerminal ? (react_1.default.createElement(icons_1.FolderClosedIcon, null)) : expanded ? (react_1.default.createElement(icons_1.FolderOpenedIcon, null)) : (react_1.default.createElement(icons_1.FolderClosedIcon, null));
        const isCheckable = typeof checkable === 'function' ? checkable(node) : checkable;
        const isNodeLoading = !disabled && loading && !warning;
        const hasWarning = !disabled && warning;
        const icon = hideIcon
            ? null
            : iconRenderer === null || iconRenderer === void 0 ? void 0 : iconRenderer({
                node,
                collapsed: !expanded,
                terminal: isTerminal,
                defaultIcon
            });
        return (react_1.default.createElement(Root, { disabled: disabled, highlight: checkableConfig.checkableHighlighting && checked, testId: rest['data-selector-id'], isTerminal: isTerminal, autoHeight: autoHeight, depth: depth, className: selected ? 'active' : '', isCheckable: isCheckable, onClick: (e) => {
                if (!disabled) {
                    onActivateNode(node);
                    if (isCheckable && checkableConfig.checkableNode) {
                        e.preventDefault();
                        onCheckedNode === null || onCheckedNode === void 0 ? void 0 : onCheckedNode(node);
                    }
                }
            }, onContextMenu: (e) => {
                if (!disabled && onContextMenu) {
                    e.preventDefault();
                    onActivateNode(node);
                    onContextMenu(node, e.nativeEvent);
                }
            }, direction: StackView_1.STACK_DIRECTION.row, distribution: StackView_1.STACK_DISTRIBUTION.lastFill, align: StackView_1.STACK_ALIGN.center },
            react_1.default.createElement(StackView_1.StackView, { direction: StackView_1.STACK_DIRECTION.row, align: align, distribution: StackView_1.STACK_DISTRIBUTION.lastFill },
                react_1.default.createElement(StackView_1.StackView, { direction: StackView_1.STACK_DIRECTION.row, align: StackView_1.STACK_ALIGN.center, distribution: StackView_1.STACK_DISTRIBUTION.lastFill },
                    !isTerminal && (react_1.default.createElement(ToggleView, { "data-selector-id": `${rest['data-selector-id']}-toggle`, contentMod: View_1.VIEW_CONTENT_MOD.center, onClick: (event) => {
                            event.stopPropagation();
                            if (!disabled) {
                                onExpandNode(node);
                            }
                        }, collapsable: false }, expanded ? (react_1.default.createElement(arrowIcons_1.NodeExpandedIcon, { "data-selector-id": `${rest['data-selector-id']}-expanded-icon`, disabled: disabled })) : (react_1.default.createElement(arrowIcons_1.NodeCollapsedIcon, { "data-selector-id": `${rest['data-selector-id']}-collapsed-icon`, disabled: disabled })))),
                    isCheckable && (react_1.default.createElement(CheckboxWrapper, { collapsable: false },
                        react_1.default.createElement(Checkbox_1.Checkbox, { onClick: (e) => {
                                if (!checkableConfig.activateNodeOnCheck) {
                                    e.stopPropagation();
                                }
                            }, onKeyPress: (e) => {
                                if (!checkableConfig.activateNodeOnCheck) {
                                    e.stopPropagation();
                                }
                            }, onChange: () => {
                                onCheckedNode === null || onCheckedNode === void 0 ? void 0 : onCheckedNode(node);
                            }, indeterminate: indeterminate, checked: checked, disabled: !!disabled, "data-selector-id": `${rest['data-selector-id']}-checkbox` }))),
                    isNodeLoading && (react_1.default.createElement(View_1.View, { indentRight: View_1.INDENT.m, "data-selector-id": `${rest['data-selector-id']}-loading` },
                        react_1.default.createElement(Spinner_1.Spinner, { size: SPINNER_SIZE }))),
                    hasWarning && (react_1.default.createElement(View_1.View, { indentRight: View_1.INDENT.m },
                        react_1.default.createElement(Tooltip_1.Tooltip, { text: warning, render: (injectProps) => (react_1.default.createElement(StatusIcon_1.StatusIcon, Object.assign({}, injectProps, { size: Icon_1.ICON_SIZES.m, status: public_1.STATUSES.warning, "data-selector-id": `${rest['data-selector-id']}-warning` }))) }))),
                    icon !== null && (react_1.default.createElement(Icon_1.Icon, { src: icon !== null && icon !== void 0 ? icon : defaultIcon, size: Icon_1.ICON_SIZES.m, disabled: disabled, "data-selector-id": `${rest['data-selector-id']}-icon` }))),
                renderNodeDescription())));
    }
}
exports.TreeNode = TreeNode;
//# sourceMappingURL=TreeNode.js.map